import _ from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {ContactsManagerSearchBarContainer} from '../../../../libs/contacts-manager/src/lib/ui/web/common/contactsManagerSearchBarContainer';
import {useInitialEffect} from '../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {MouseEventHandler} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {DataEventTrackingProvider} from '../../../../libs/shared/core/src/services/tracking/dataEventTrackingProvider';
import {fetchKnowledgeBasesAsyncAction} from '../../../../libs/shared/core/src/store/data/knowledgeBases/knowledgeBasesActions';
import {getTheme} from '../../../../libs/shared/core/src/store/data/preferences/commonPreferencesSelectors';
import {getShouldHideAuditTrail} from '../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {AppContentTypesEnum} from '../../../../libs/shared/core/src/store/panels/app/commonAppPanelState';
import {
  AppPanelId,
  makeContactsManagerPanelId,
  makeInboxesPanelId,
  makeLegacyPanelId,
} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {createSelector} from '../../../../srcCommon/helpers/reselectHelpers';
import {useAppDispatch, useAppSelector} from '../../../helpers/appReduxHelpers';
import {PermissionsEnum} from '../../../models/api/roleModel';
import {
  ElectronPoint,
  handleTitleBarDoubleClickAsync,
  hideElectronDragRegionsAsync,
} from '../../../services/electron/electronBridgeOutbound';
import {getHasPermission, getHasSharedPermission} from '../../../store/data/access/accessSelectors';
import {getHasAccessToContactsManager} from '../../../store/data/circles/circlesSelectors';
import {getHasAccessToAnyKnowledgeBase} from '../../../store/data/knowledgeBases/knowledgeBasesSelectors';
import {getUserNamespace} from '../../../store/data/teammates/teammatesSelectors';
import {getCanHideWindowsTitleBar, getIsElectron} from '../../../store/electron/electronSelectors';
import {InboxesSearchBarContainer} from '../../inboxes/search/searchBar/inboxesSearchBarContainer';
import {SettingsSearchBarContainer} from '../../settings/search/settingsSearchBarContainer';
import {AppSwitcher} from './appSwitcher';
import {MacOsTrafficLights} from './macOsTrafficLights';
import {useMaybeOpenOnboardingModalUponInitialLoad} from './onboarding/onboardingHooks';
import {SettingsSection} from './settingsSection';
import {TopBarExpandedDarkThemeProvider} from './topBarExpandedDarkThemeProvider';
import {
  buildApps,
  findTopBarBrandColor,
  maxTopBarSearchBarWidth,
  minTopBarSearchBarWidth,
  shouldOverrideTopBarBrandColor,
  topBarButtonGap,
  topBarHeight,
  topBarHorizontalPadding,
  topBarSearchBarFlexBasis,
  topBarSideFlexBasis,
  topBarVerticalPadding,
} from './topBarHelpers';
import {WindowsTitleBarControls} from './windowsTitleBar/windowsTitleBarControls';
import {WindowsTitleBarMenuButton} from './windowsTitleBar/windowsTitleBarMenuButton';

/*
 * Constants.
 */

const macOsTrafficLightPosition: ElectronPoint = {
  // Offset by 1px to visually match.
  x: topBarHorizontalPadding - 1,
  // Determined experimentally. (This is probably weird because macOS's y-axis is flipped.)
  y: 18,
};

/*
 * Props.
 */

interface TopBarProps {
  className?: string;
  panelId: AppPanelId;
  contentType: AppContentTypesEnum;
}

/*
 * Styles.
 */

interface StyledTopBarWrapperProps {
  $isDarkBrandColorOverride: boolean;
  $canHideWindowsTitleBar: boolean;
}

const StyledTopBarWrapper = styled.div<StyledTopBarWrapperProps>`
  -webkit-app-region: drag;

  display: flex;
  /* If stuff can't fit anymore, prioritize the Windows controls at the end. */
  justify-content: ${(p) => (p.$canHideWindowsTitleBar ? 'flex-end' : 'flex-start')};
  /* Use the same gap between search bar and buttons as between buttons. */
  gap: ${topBarButtonGap}px;
  align-items: center;

  height: ${topBarHeight}px;
  /* The horizontal padding should be even to keep the search bar centered. */
  padding: ${topBarVerticalPadding}px ${topBarHorizontalPadding}px;
  contain: strict;

  background-color: ${(p) => findTopBarBrandColor(p.theme, p.$isDarkBrandColorOverride)};
`;

const StyledTopBarSideWrapper = styled.div`
  flex: 1 1 ${topBarSideFlexBasis}px;

  display: flex;
`;

const StyledTopBarSearchBarWrapper = styled.div`
  flex: 1 1 ${topBarSearchBarFlexBasis}px;
  min-width: ${minTopBarSearchBarWidth}px;
  max-width: ${maxTopBarSearchBarWidth}px;
`;

const StyledMacOsTrafficLights = styled(MacOsTrafficLights)`
  margin-right: 32px;
`;

const StyledWindowsTitleBarMenu = styled(WindowsTitleBarMenuButton)`
  margin-left: -7px;
  padding-right: 8px;
  margin-right: 8px;

  border-right: 1px solid ${(p) => p.theme.alphas.white20};
`;

const StyledWindowsControls = styled(WindowsTitleBarControls)`
  margin: ${-topBarVerticalPadding}px ${-topBarHorizontalPadding}px ${-topBarVerticalPadding}px
    ${topBarHorizontalPadding}px;
`;

/*
 * Component.
 */

export const TopBar: FC<TopBarProps> = ({className, panelId, contentType}) => {
  const isElectron = useAppSelector(getIsElectron);
  const shouldHideAuditTrail = useAppSelector(getShouldHideAuditTrail);
  const shouldDisplayContactsManager = useAppSelector(getHasAccessToContactsManager);
  const shouldDisplayKnowledgeBase = useAppSelector(getHasAccessToAnyKnowledgeBase);
  const userThemePreference = useAppSelector(getTheme);
  const canHideWindowsTitleBar = useAppSelector(getCanHideWindowsTitleBar);
  const dispatch = useAppDispatch();

  useInitialEffect(() => {
    dispatch(fetchKnowledgeBasesAsyncAction());
  });

  useMaybeOpenOnboardingModalUponInitialLoad(panelId);

  const apps = buildApps({
    shouldDisplayContactsManager,
    shouldDisplayAuditTrail: !shouldHideAuditTrail,
    shouldDisplayKnowledgeBase,
  });

  const hasSharedOrImplicitPermission = useAppSelector(getHasSharedOrImplicitPermission);
  const allowedApps = apps.filter((app) => !app.permission || hasSharedOrImplicitPermission(app.permission));

  // TODO: Simplify me when contacts manager is released
  const selectedApp = _(allowedApps)
    .compact()
    .find((app) => app.contentType === contentType);

  useInitialEffect(() => {
    if (!isElectron) {
      return;
    }

    // Tell Electron to hide its drag regions, since we render our own.
    hideElectronDragRegionsAsync();
  });

  const onDoubleClick: MouseEventHandler = (event) => {
    if (!isElectron || event.defaultPrevented) {
      return;
    }

    event.preventDefault();
    handleTitleBarDoubleClickAsync();
  };

  return (
    <DataEventTrackingProvider context={{name: 'p0', payload: {}}}>
      <TopBarExpandedDarkThemeProvider appContentType={contentType}>
        <StyledTopBarWrapper
          className={className}
          $isDarkBrandColorOverride={shouldOverrideTopBarBrandColor(userThemePreference, contentType)}
          $canHideWindowsTitleBar={canHideWindowsTitleBar}
          onDoubleClick={onDoubleClick}
        >
          <StyledTopBarSideWrapper>
            <StyledMacOsTrafficLights position={macOsTrafficLightPosition} />
            <StyledWindowsTitleBarMenu />
            <AppSwitcher panelId={panelId} apps={allowedApps} selectedApp={selectedApp} />
          </StyledTopBarSideWrapper>
          <StyledTopBarSearchBarWrapper>
            {maybeRenderSearchBar(panelId, contentType)}
          </StyledTopBarSearchBarWrapper>
          <StyledTopBarSideWrapper>
            <SettingsSection
              panelId={panelId}
              isSettingsAppSelected={contentType === AppContentTypesEnum.SETTINGS}
            />
            <StyledWindowsControls />
          </StyledTopBarSideWrapper>
        </StyledTopBarWrapper>
      </TopBarExpandedDarkThemeProvider>
    </DataEventTrackingProvider>
  );
};

/*
 * Selectors.
 */

export const getHasSharedOrImplicitPermission = createSelector(
  [getHasPermission, getHasSharedPermission, getUserNamespace],
  (hasPermission, hasSharedPermission, userNamespace) => (permission: PermissionsEnum) =>
    hasSharedPermission(permission) || hasPermission(userNamespace, permission),
);

/*
 * Helpers.
 */

// This implementation is not ideal as we're tying P0 with apps.
// This should be revisited when we'll split bundles.
const maybeRenderSearchBar = (panelId: AppPanelId, contentType: AppContentTypesEnum) => {
  switch (contentType) {
    case AppContentTypesEnum.INBOXES:
      return <InboxesSearchBarContainer panelId={makeInboxesPanelId(panelId)} />;
    case AppContentTypesEnum.CONTACTS:
      return <ContactsManagerSearchBarContainer panelId={makeContactsManagerPanelId(panelId)} />;
    case AppContentTypesEnum.SETTINGS:
      return <SettingsSearchBarContainer panelId={makeLegacyPanelId(panelId)} />;
    default:
      return null;
  }
};
